// Tiny analytics helper for GA4 + visitor identification.
// Safe no-op if gtag is not available.
//
// Funnel events (D4) are also beaconed to /api/analytics/track so the admin
// dashboard has a real conversion funnel that doesn't depend on GA4 reporting.

const COOKIE_NAME = "ashlan_vid";
const COOKIE_DAYS = 730; // 2 years
const SESSION_KEY = "ashlan_sid";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Events we mirror to the backend for the admin funnel dashboard.
const FUNNEL_EVENTS = new Set([
  "page_view",
  "blog_engaged_read",
  "book_now_click",
  "contact_start",
  "contact_submit",
  "form_start",
  "form_submit",
  "form_abandon",
  "product_request_open",
  "product_request_submit",
  "share_experience_open",
  "testimonial_submit",
  "share_click",
  "corporate_pdf_download",
  "corporate_inquire_click",
  "learning_hub_tab_view",
  "learning_hub_resource_click",
]);

function _uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function _getCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : "";
}

function _setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie =
    name +
    "=" +
    encodeURIComponent(value) +
    "; expires=" +
    d.toUTCString() +
    "; path=/; SameSite=Lax";
}

export function getVisitorId() {
  if (typeof document === "undefined") return "";
  let id = _getCookie(COOKIE_NAME);
  if (!id) {
    id = _uuid();
    _setCookie(COOKIE_NAME, id, COOKIE_DAYS);
  }
  return id;
}

export function getSessionId() {
  if (typeof sessionStorage === "undefined") return "";
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = _uuid();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

// Initialise GA4 with visitor identity (call once on first load)
export function initAnalytics() {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const vid = getVisitorId();
  const sid = getSessionId();
  try {
    window.gtag("set", "user_properties", {
      ashlan_visitor_id: vid,
      ashlan_session_id: sid,
    });
    window.gtag("set", { user_id: vid });
  } catch (err) {
    // GA4 should never break the app — surface to console for debugging only.
    console.warn("initAnalytics failed:", err);
  }
}

// Fire a custom event with extra params
export function track(eventName, params = {}) {
  // 1. GA4 (no-op if gtag isn't loaded).
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    try {
      window.gtag("event", eventName, {
        ashlan_visitor_id: getVisitorId(),
        page_path: window.location.pathname,
        ...params,
      });
    } catch (err) {
      console.warn("track() failed for event", eventName, err);
    }
  }

  // 2. Matomo (no-op if _paq isn't loaded).
  //    Maps to Matomo's trackEvent(category, action, name, value) signature.
  //    Category is a stable bucket; action is the event name; name carries
  //    the most useful single dimension; value is numeric where it makes sense.
  if (typeof window !== "undefined" && Array.isArray(window._paq)) {
    try {
      const category = matomoCategoryFor(eventName);
      const name = matomoNameFor(eventName, params);
      const value = typeof params.value === "number" ? params.value : undefined;
      const args = ["trackEvent", category, eventName];
      if (name !== undefined) args.push(name);
      if (value !== undefined) args.push(value);
      window._paq.push(args);
      // Also push custom dimensions so Matomo can group by them in reports.
      if (params.form_name) window._paq.push(["setCustomDimension", 1, params.form_name]);
      if (params.share_destination) window._paq.push(["setCustomDimension", 2, params.share_destination]);
      if (params.tab) window._paq.push(["setCustomDimension", 3, params.tab]);
    } catch (err) {
      console.warn("matomo track failed", err);
    }
  }

  // 3. Backend funnel beacon (D4) — only for whitelisted funnel events.
  if (FUNNEL_EVENTS.has(eventName) && BACKEND_URL && typeof navigator !== "undefined") {
    try {
      const body = JSON.stringify({
        event_name: eventName,
        page_path: typeof window !== "undefined" ? window.location.pathname : "",
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        params,
      });
      const url = `${BACKEND_URL}/api/analytics/track`;
      // sendBeacon survives navigation away (critical for form_abandon).
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    } catch (err) {
      // Analytics must never break the app.
      console.warn("backend beacon failed", err);
    }
  }
}

// Group events into Matomo categories so reports stay readable.
function matomoCategoryFor(eventName) {
  if (eventName.startsWith("form_") || eventName.endsWith("_submit") || eventName.endsWith("_start") || eventName.endsWith("_open"))
    return "Forms";
  if (eventName.startsWith("concierge_")) return "Concierge";
  if (eventName.startsWith("learning_hub_")) return "Learning Hub";
  if (eventName.startsWith("corporate_")) return "Corporate";
  if (eventName.startsWith("blog_") || eventName === "page_view") return "Content";
  if (eventName === "share_click") return "Sharing";
  if (eventName === "book_now_click") return "Booking";
  if (eventName.includes("match") || eventName === "symptom_check_complete") return "Quiz";
  return "Site";
}

// Pick the most useful single descriptor for Matomo's "name" slot.
function matomoNameFor(eventName, params) {
  return (
    params.form_name ||
    params.share_destination ||
    params.tab ||
    params.product_name ||
    params.programme ||
    params.intent ||
    params.category ||
    params.slug ||
    undefined
  );
}

// SPA route-change page view tracker — call this from a router effect when
// the pathname changes. Matomo's snippet only auto-fires the initial view.
export function trackPageView(path, title) {
  if (typeof window === "undefined") return;
  if (Array.isArray(window._paq)) {
    try {
      window._paq.push(["setCustomUrl", path]);
      window._paq.push(["setDocumentTitle", title || document.title]);
      window._paq.push(["deleteCustomVariables", "page"]);
      window._paq.push(["trackPageView"]);
    } catch (err) {
      console.warn("matomo pageview failed", err);
    }
  }
  if (typeof window.gtag === "function") {
    try {
      window.gtag("event", "page_view", {
        page_path: path,
        page_title: title || document.title,
      });
    } catch (e) { /* noop */ }
  }
}
